// @ts-nocheck
/*
 * Port of session-replay/recording-settings.js (193 lines). BYTE-IDENTICAL.
 * Per-agent recording toggle list. Loads agents + saved recording config in
 * parallel, tracks a working `enabledMap` vs the persisted `initialMap` and
 * only sends the diff (dirty items) on Save. Search filters the visible
 * list client-side. Empty save is a noop with an info toast.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Card as ShadCard } from '@/components/ui/card';
import { Button as _ShadButton } from '@/components/ui/button';

const _cx = (...args: any[]) => args.filter(Boolean).join(' ');
const _btnVariantMap: Record<string, any> = {
  primary: 'default',
  secondary: 'outline',
  danger: 'destructive',
  light: 'outline',
  link: 'link',
};
const Button: any = ({ variant, size, className, children, ...props }: any) => {
  let mapped = variant;
  if (typeof variant === 'string') {
    if (variant.startsWith('outline-')) mapped = 'outline';
    else if (_btnVariantMap[variant]) mapped = _btnVariantMap[variant];
    else if (variant.startsWith('btn ')) mapped = 'default';
  }
  return (
    <_ShadButton variant={mapped} size={size} className={className} {...props}>
      {children}
    </_ShadButton>
  );
};
const Card: any = ({ className, children, ...props }: any) => (
  <ShadCard className={className} {...props}>
    {children}
  </ShadCard>
);
Card.Header = ({ className, children, ...props }: any) => (
  <div className={_cx('card-header', className)} {...props}>
    {children}
  </div>
);
Card.Body = ({ className, children, ...props }: any) => (
  <div className={_cx('card-body', className)} {...props}>
    {children}
  </div>
);
Card.Title = ({ as: As = 'h5', className, children, ...props }: any) => (
  <As className={_cx('card-title', className)} {...props}>
    {children}
  </As>
);
const Form: any = ({ children, onSubmit, className, ...props }: any) => (
  <form
    className={className}
    onSubmit={onSubmit || ((e: any) => e.preventDefault())}
    {...props}
  >
    {children}
  </form>
);
Form.Group = ({ className, controlId: _c, children, ...props }: any) => (
  <div className={_cx('mb-3', className)} {...props}>
    {children}
  </div>
);
Form.Label = ({ className, children, htmlFor, ...props }: any) => (
  <label className={_cx('form-label', className)} htmlFor={htmlFor} {...props}>
    {children}
  </label>
);
Form.Control = ({ as, className, children, isInvalid, ...props }: any) => {
  const cls = _cx('form-control', isInvalid && 'is-invalid', className);
  if (as === 'select')
    return (
      <select className={cls} {...props}>
        {children}
      </select>
    );
  if (as === 'textarea') return <textarea className={cls} {...props} />;
  return <input className={cls} {...props} />;
};
Form.Select = ({ className, children, isInvalid, ...props }: any) => (
  <select
    className={_cx('form-control form-select', isInvalid && 'is-invalid', className)}
    {...props}
  >
    {children}
  </select>
);
Form.Check = ({
  type = 'checkbox',
  label,
  id,
  className,
  inline,
  children,
  ...props
}: any) => (
  <div className={_cx('form-check', inline && 'form-check-inline', className)}>
    <input type={type} id={id} className="form-check-input" {...props} />
    {label !== undefined && (
      <label htmlFor={id} className="form-check-label">
        {label}
      </label>
    )}
    {children}
  </div>
);
const Badge: any = ({ bg, pill, className, children, ...props }: any) => (
  <span
    className={_cx('badge', bg && `bg-${bg}`, pill && 'rounded-pill', className)}
    {...props}
  >
    {children}
  </span>
);
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { useNavigate } from 'react-router-dom';
import { getApiCall, postApiCall } from '@/service/httpCall';
import {
  API_AGENTS_LIST,
  API_SESSION_REPLAY_RECORDING_CONFIG,
} from '@/service/url';
import Loader from '@/components/partials/components/Loader';
import { ScrollPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { ArrowLeft, Search, Save, User } from 'lucide-react';

function SessionReplayRecordingSettings() {
  const navigate = useNavigate();

  const [agents, setAgents] = useState<any[]>([]);
  const [enabledMap, setEnabledMap] = useState<any>({}); // agent_id -> bool (working state)
  const [initialMap, setInitialMap] = useState<any>({}); // agent_id -> bool (server state)
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [query, setQuery] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [agentsResp, configResp]: any = await Promise.all([
        getApiCall(API_AGENTS_LIST, { fields: 'min' }),
        getApiCall(API_SESSION_REPLAY_RECORDING_CONFIG, {}),
      ]);

      const list = Array.isArray(agentsResp)
        ? agentsResp
        : agentsResp?.rows || agentsResp?.data || [];
      const cleanAgents = list
        .filter((a: any) => a && a.id != null)
        .map((a: any) => ({
          id: Number(a.id),
          agent_name: a.agent_name || a.name || `Agent #${a.id}`,
          agent_number: a.agent_number || '',
        }));

      const settings = Array.isArray(configResp?.settings) ? configResp.settings : [];
      const map: any = {};
      for (const s of settings) map[Number(s.agent_id)] = Boolean(s.enabled);

      setAgents(cleanAgents);
      setInitialMap(map);
      setEnabledMap(map);
    } catch (e: any) {
      toast.error(friendlyApiError(e) || 'Failed to load recording settings');
      setAgents([]);
      setEnabledMap({});
      setInitialMap({});
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const toggle = (agentId: number) => {
    setEnabledMap((prev: any) => ({ ...prev, [agentId]: !prev[agentId] }));
  };

  const dirtyItems = useMemo(() => {
    const items: any[] = [];
    for (const a of agents) {
      const cur = Boolean(enabledMap[a.id]);
      const init = Boolean(initialMap[a.id]);
      if (cur !== init) items.push({ agent_id: a.id, enabled: cur });
    }
    return items;
  }, [agents, enabledMap, initialMap]);

  const enabledCount = useMemo(
    () => agents.filter((a: any) => enabledMap[a.id]).length,
    [agents, enabledMap],
  );

  const save = async () => {
    if (!dirtyItems.length) {
      toast.info('No changes to save');
      return;
    }
    setSaving(true);
    try {
      await postApiCall(API_SESSION_REPLAY_RECORDING_CONFIG, { items: dirtyItems });
      toast.success(`Updated recording for ${dirtyItems.length} agent(s)`);
      setInitialMap({ ...enabledMap });
    } catch (e: any) {
      toast.error(friendlyApiError(e) || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const filteredAgents = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return agents;
    return agents.filter(
      (a: any) =>
        a.agent_name.toLowerCase().includes(q) ||
        String(a.agent_number).toLowerCase().includes(q),
    );
  }, [agents, query]);

  return (
    <ScrollPage>
      <PageHeader
        title="Recording Settings"
        description={`Only agents toggled on here have their dashboard sessions recorded. ${enabledCount} agent(s) currently recording.`}
        actions={
          <Button variant="outline" onClick={() => navigate('/session-replay')}>
            <ArrowLeft className="h-4 w-4" />
            Back
          </Button>
        }
      />
      <div
        className="mb-3 flex items-start gap-2 rounded-lg border border-primary/25 bg-primary/[0.06] px-3 py-2 text-xs text-foreground"
        role="alert"
      >
        <span className="mt-0.5 shrink-0 text-primary">ⓘ</span>
        <span>
          Only agents toggled <strong>on</strong> here will have their dashboard
          sessions recorded. Changes apply the next time the agent loads the
          dashboard.
        </span>
      </div>

      <div
        className="mb-3 flex flex-wrap items-center gap-2 rounded-xl border border-border/60 bg-card px-3 py-2.5"
        style={{
          boxShadow:
            '6px 6px 14px rgba(163, 177, 198, 0.35), -6px -6px 14px rgba(255, 255, 255, 0.9)',
        }}
      >
        <div className="relative w-full max-w-[320px]">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            placeholder="Search agents…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="input-embossed h-9 w-full rounded-md pl-8 pr-3 text-xs"
          />
        </div>
        <div className="ml-auto flex items-center gap-3">
          <span className="text-xs text-muted-foreground">
            <span className="font-semibold text-foreground">{enabledCount}</span> of{' '}
            <span className="font-semibold text-foreground">{agents.length}</span>{' '}
            recording
          </span>
          <button
            type="button"
            onClick={save}
            disabled={saving || !dirtyItems.length}
            className="inline-flex h-9 items-center justify-center gap-1.5 rounded-md border border-primary bg-primary px-4 text-xs font-bold text-primary-foreground shadow-[3px_3px_6px_rgba(163,177,198,0.4),-3px_-3px_6px_rgba(255,255,255,0.9)] transition-colors hover:bg-primary/90 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
          >
            <Save className="h-3.5 w-3.5" />
            {saving
              ? 'Saving…'
              : `Save${dirtyItems.length ? ` (${dirtyItems.length})` : ''}`}
          </button>
        </div>
      </div>

      {loading ? (
        <Loader />
      ) : !filteredAgents.length ? (
        <div className="flex flex-col items-center gap-2.5 rounded-xl border border-border/60 bg-muted/30 px-5 py-12 text-center">
          <span className="inline-flex h-12 w-12 items-center justify-center rounded-2xl bg-primary/10 text-primary ring-1 ring-inset ring-primary/20">
            <User className="h-6 w-6" />
          </span>
          <div className="text-sm font-medium text-muted-foreground">
            {query ? `No agents match “${query}”.` : 'No agents found.'}
          </div>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-border/60 bg-card">
          {filteredAgents.map((a: any, i: number) => {
            const isOn = Boolean(enabledMap[a.id]);
            return (
              <div
                key={a.id}
                className={
                  'flex items-center gap-3 px-4 py-3 transition-colors hover:bg-muted/30 ' +
                  (i === 0 ? '' : 'border-t border-border/40')
                }
              >
                <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary ring-1 ring-inset ring-primary/20">
                  <User className="h-4 w-4" />
                </span>
                <div className="min-w-0 flex-1">
                  <div className="truncate text-sm font-semibold text-foreground">
                    {a.agent_name}
                  </div>
                  {a.agent_number ? (
                    <div className="text-xs text-muted-foreground">
                      {a.agent_number}
                    </div>
                  ) : null}
                </div>
                <div className="flex items-center gap-2.5">
                  <span
                    className={
                      'text-xs font-semibold ' +
                      (isOn ? 'text-emerald-700 dark:text-emerald-400' : 'text-muted-foreground')
                    }
                  >
                    {isOn ? 'Recording' : 'Off'}
                  </span>
                  <button
                    type="button"
                    onClick={() => toggle(a.id)}
                    aria-pressed={isOn}
                    aria-label={
                      isOn ? `Disable recording for ${a.agent_name}` : `Enable recording for ${a.agent_name}`
                    }
                    className={
                      'relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full transition-colors ring-1 ring-inset ' +
                      (isOn
                        ? 'bg-emerald-500 ring-emerald-600'
                        : 'bg-muted/70 ring-border/70')
                    }
                  >
                    <span
                      className={
                        'inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ' +
                        (isOn ? 'translate-x-[18px]' : 'translate-x-[2px]')
                      }
                    />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </ScrollPage>
  );
}

export default SessionReplayRecordingSettings;
